import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  School,
  Teacher,
  getSchool,
  getSchools,
  resolveTeacherClassId,
} from '../../utils/firebase';
import { db } from '../../utils/firebase';
import {
  collection,
  deleteField,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

type ClassKey = string;

function classKey(grade: number, classNum: number): ClassKey {
  return `${grade}_${classNum}`;
}

function expectedClassesFromSchool(gradeClasses: School['gradeClasses']): Array<{ grade: number; class: number }> {
  const out: Array<{ grade: number; class: number }> = [];
  const gc = gradeClasses || {};
  for (const [gStr, count] of Object.entries(gc)) {
    const g = Number(gStr);
    const n = Number(count) || 0;
    for (let c = 1; c <= n; c++) {
      out.push({ grade: g, class: c });
    }
  }
  return out.sort((a, b) => a.grade - b.grade || a.class - b.class);
}

function isInGradeClassConfig(
  gradeClasses: School['gradeClasses'],
  grade: number,
  classNum: number
): boolean {
  const max = gradeClasses?.[grade];
  return max != null && classNum >= 1 && classNum <= max;
}

/** schools/{schoolId}/teachers 스냅샷을 Teacher 형태로 */
function mapSchoolTeacherDoc(docId: string, data: Record<string, unknown>): Teacher {
  const homeroom = data.homeroomClass as { grade?: number; class?: number } | undefined;
  return {
    uid: docId,
    email: (data.email as string) || '',
    name: (data.name as string) || '',
    role: (data.role as Teacher['role']) || 'teacher',
    schoolId: (data.schoolId as string) || '',
    subject: (data.subject as string) || '',
    subjectId: (data.subjectId as string) || '',
    homeroomClass:
      homeroom && homeroom.grade != null && homeroom.class != null
        ? { grade: Number(homeroom.grade), class: Number(homeroom.class) }
        : undefined,
    stampClasses: (data.stampClasses as Teacher['stampClasses']) || [],
    classRoles: (data.classRoles as string[]) || [],
    classId: (data.classId as string) || '',
    isActive: data.isActive !== false,
    createdAt: (data.createdAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    updatedAt: (data.updatedAt as { toDate?: () => Date })?.toDate?.() || new Date(),
    loginType: (data.loginType as Teacher['loginType']) || 'local',
  };
}

const HomeroomTeacherManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const [schools, setSchools] = useState<School[]>([]);
  const [selectedSchoolId, setSelectedSchoolId] = useState<string>('');
  const [schoolDetail, setSchoolDetail] = useState<School | null>(null);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [clearingUid, setClearingUid] = useState<string | null>(null);

  const isSuperAdmin = currentUser?.role === 'superAdmin';
  const schoolAdminSchoolId = currentUser?.role === 'schoolAdmin' ? currentUser.schoolId : '';

  const loadSchoolList = useCallback(async () => {
    try {
      setLoadingSchools(true);
      setError(null);
      const list = await getSchools();
      if (currentUser?.role === 'schoolAdmin' && currentUser.schoolId) {
        const one = list.find((s) => s.id === currentUser.schoolId);
        setSchools(one ? [one] : []);
        setSelectedSchoolId(currentUser.schoolId);
      } else {
        setSchools(list);
        if (list.length === 1) {
          setSelectedSchoolId(list[0].id);
        }
      }
    } catch (e) {
      console.error(e);
      setError('학교 목록을 불러오지 못했습니다.');
    } finally {
      setLoadingSchools(false);
    }
  }, [currentUser?.role, currentUser?.schoolId]);

  useEffect(() => {
    loadSchoolList();
  }, [loadSchoolList]);

  const loadSchoolTeachers = useCallback(async (schoolId: string) => {
    if (!schoolId) {
      setSchoolDetail(null);
      setTeachers([]);
      return;
    }
    try {
      setLoadingDetail(true);
      setError(null);
      const [detail, snap] = await Promise.all([
        getSchool(schoolId),
        getDocs(collection(db, `schools/${schoolId}/teachers`)),
      ]);
      setSchoolDetail(detail);
      setTeachers(snap.docs.map((d) => mapSchoolTeacherDoc(d.id, d.data() as Record<string, unknown>)));
    } catch (e) {
      console.error(e);
      setError('학교·교사 정보를 불러오지 못했습니다.');
      setSchoolDetail(null);
      setTeachers([]);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  useEffect(() => {
    if (selectedSchoolId) {
      loadSchoolTeachers(selectedSchoolId);
    }
  }, [selectedSchoolId, loadSchoolTeachers]);

  const homeroomTeachers = useMemo(() => {
    return teachers.filter((t) => t.homeroomClass && t.homeroomClass.grade > 0 && t.homeroomClass.class > 0);
  }, [teachers]);

  const byClass = useMemo(() => {
    const map = new Map<ClassKey, Teacher[]>();
    for (const t of homeroomTeachers) {
      const hc = t.homeroomClass!;
      const key = classKey(hc.grade, hc.class);
      const arr = map.get(key) || [];
      arr.push(t);
      map.set(key, arr);
    }
    return map;
  }, [homeroomTeachers]);

  const expectedRows = useMemo(() => {
    if (!schoolDetail) return [];
    return expectedClassesFromSchool(schoolDetail.gradeClasses);
  }, [schoolDetail]);

  const outOfConfigTeachers = useMemo(() => {
    if (!schoolDetail) return [];
    const gc = schoolDetail.gradeClasses || {};
    return homeroomTeachers.filter((t) => {
      const hc = t.homeroomClass!;
      return !isInGradeClassConfig(gc, hc.grade, hc.class);
    });
  }, [homeroomTeachers, schoolDetail]);

  const handleClearHomeroom = async (teacher: Teacher) => {
    const schoolId = selectedSchoolId || teacher.schoolId;
    if (!schoolId || !teacher.uid || !teacher.homeroomClass) {
      setError('담임 정보가 올바르지 않습니다.');
      return;
    }

    const classId =
      resolveTeacherClassId({ ...teacher, schoolId }) ||
      `${schoolId}_${teacher.homeroomClass.grade}_${teacher.homeroomClass.class}`;

    const msg = `「${teacher.name}」 교사의 담임(${teacher.homeroomClass.grade}학년 ${teacher.homeroomClass.class}반) 연결을 해제할까요? 학급·학생 데이터는 삭제되지 않습니다.`;
    if (!window.confirm(msg)) return;

    try {
      setClearingUid(teacher.uid);
      setError(null);
      setSuccess(null);

      const teacherRef = doc(db, `schools/${schoolId}/teachers/${teacher.uid}`);
      const classRef = doc(db, `schools/${schoolId}/classes/${classId}`);
      const classSnap = await getDoc(classRef);

      const batch = writeBatch(db);
      batch.update(teacherRef, {
        homeroomClass: deleteField(),
        classId: deleteField(),
        updatedAt: serverTimestamp(),
      });

      if (classSnap.exists()) {
        batch.update(classRef, {
          teacherId: null,
          teacherName: null,
          updatedAt: serverTimestamp(),
        });
      }

      await batch.commit();
      setSuccess('담임 연결이 해제되었습니다. 해당 교사는 설정에서 새 담임 학급을 다시 지정할 수 있습니다.');
      await loadSchoolTeachers(schoolId);
    } catch (e) {
      console.error(e);
      setError('담임 해제 중 오류가 발생했습니다.');
    } finally {
      setClearingUid(null);
    }
  };

  if (!isSuperAdmin && !schoolAdminSchoolId) {
    return (
      <div style={{ padding: '24px' }}>
        <Card padding="20px">
          <div style={{ color: 'var(--Color-text-strong)' }}>학교 관리자 계정에 학교 정보가 없습니다. 관리자에게 문의해 주세요.</div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1
          style={{
            fontSize: '28px',
            fontWeight: 600,
            lineHeight: '42px',
            color: 'var(--Color-text-strong)',
            margin: '0 0 8px 0',
          }}
        >
          학급 담임 교사 관리
        </h1>
        <p
          style={{
            fontSize: '15px',
            lineHeight: '22px',
            color: 'var(--Color-text-normal)',
            margin: 0,
            maxWidth: '720px',
          }}
        >
          학교에 등록된 학급 구성 기준으로 담임이 연결된 교사를 확인하고, 학년도가 바뀌었을 때 이전 담임 정보가 남은 경우 여기서 연결을 해제할 수 있습니다. 해제 후에는 해당 교사가「설정 → 담임 설정」에서 새 학급을 다시 지정해야 합니다.
        </p>
      </div>

      {error && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'var(--Color-status-negative-background, #FDECEC)',
            color: 'var(--Color-status-negative, #C62828)',
            fontSize: '14px',
          }}
        >
          {error}
        </div>
      )}
      {success && (
        <div
          style={{
            marginBottom: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'var(--Color-status-positive-background, #E8F5E9)',
            color: 'var(--Color-status-positive, #2E7D32)',
            fontSize: '14px',
          }}
        >
          {success}
        </div>
      )}

      <Card padding="20px" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
          <div style={{ minWidth: '240px', flex: '1 1 280px' }}>
            <label
              htmlFor="homeroom-school-select"
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                marginBottom: '8px',
                color: 'var(--Color-text-strong)',
              }}
            >
              학교
            </label>
            <select
              id="homeroom-school-select"
              value={selectedSchoolId}
              onChange={(e) => {
                setSelectedSchoolId(e.target.value);
                setSuccess(null);
                setError(null);
              }}
              disabled={loadingSchools || (!isSuperAdmin && !!schoolAdminSchoolId)}
              style={{
                width: '100%',
                padding: '10px 12px',
                fontSize: '15px',
                borderRadius: '8px',
                border: '1px solid var(--Color-line-normal)',
                background: 'var(--Color-background-normal)',
                color: 'var(--Color-text-strong)',
              }}
            >
              <option value="">학교를 선택하세요</option>
              {schools.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.code})
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {loadingSchools || loadingDetail ? (
        <div style={{ color: 'var(--Color-text-normal)' }}>불러오는 중…</div>
      ) : !selectedSchoolId ? (
        <div style={{ color: 'var(--Color-text-normal)' }}>학교를 선택하면 담임 현황이 표시됩니다.</div>
      ) : (
        <>
          {schoolDetail && (!schoolDetail.gradeClasses || Object.keys(schoolDetail.gradeClasses).length === 0) && (
            <Card padding="16px" style={{ marginBottom: '16px', background: 'var(--Color-background-main)' }}>
              <div style={{ fontSize: '14px', color: 'var(--Color-text-normal)' }}>
                이 학교에「학년별 학급 수」가 아직 없습니다.{' '}
                <strong style={{ color: 'var(--Color-text-strong)' }}>학교 관리</strong>에서 학급 구성을 먼저 저장해 주세요.
              </div>
            </Card>
          )}

          <Card padding="0" style={{ overflow: 'hidden', marginBottom: '20px' }}>
            <div
              style={{
                padding: '16px 20px',
                borderBottom: '1px solid var(--Color-line-normal)',
                fontWeight: 600,
                fontSize: '16px',
                color: 'var(--Color-text-strong)',
              }}
            >
              학교 학급 구성 기준 담임 현황
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: 'var(--Color-background-main)' }}>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>학년·반</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>담임으로 연결된 교사</th>
                    <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>비고</th>
                    <th style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 600 }}>작업</th>
                  </tr>
                </thead>
                <tbody>
                  {expectedRows.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: '20px 16px', color: 'var(--Color-text-normal)' }}>
                        표시할 학급이 없습니다. 학교 관리에서 학년별 반 수를 설정해 주세요.
                      </td>
                    </tr>
                  ) : (
                    expectedRows.map((row) => {
                      const key = classKey(row.grade, row.class);
                      const assigned = byClass.get(key) || [];
                      const duplicate = assigned.length > 1;
                      return (
                        <tr key={key} style={{ borderTop: '1px solid var(--Color-line-normal)' }}>
                          <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                            {row.grade}학년 {row.class}반
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {assigned.length === 0 ? (
                              <span style={{ color: 'var(--Color-text-normal)' }}>—</span>
                            ) : (
                              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                                {assigned.map((t) => (
                                  <li key={t.uid} style={{ marginBottom: '4px' }}>
                                    {t.name}{' '}
                                    <span style={{ color: 'var(--Color-text-normal)', fontSize: '13px' }}>
                                      ({t.email || t.uid})
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td style={{ padding: '12px 16px', color: duplicate ? 'var(--Color-status-warning, #E65100)' : 'var(--Color-text-normal)' }}>
                            {duplicate ? '같은 반에 담임이 둘 이상 연결됨' : ''}
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {assigned.map((t) => (
                              <span key={t.uid} style={{ display: 'inline-block', marginLeft: '8px' }}>
                                <Button
                                  variant="line"
                                  size="small"
                                  onClick={() => handleClearHomeroom(t)}
                                  disabled={clearingUid === t.uid}
                                >
                                  {clearingUid === t.uid ? '처리 중…' : '담임 해제'}
                                </Button>
                              </span>
                            ))}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          {outOfConfigTeachers.length > 0 && (
            <Card padding="0" style={{ overflow: 'hidden', marginBottom: '20px' }}>
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid var(--Color-line-normal)',
                  fontWeight: 600,
                  fontSize: '16px',
                  color: 'var(--Color-status-warning, #E65100)',
                }}
              >
                학교 학급 구성에 없는 반에 연결된 담임 (학년도 변경 후 정리 필요)
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ background: 'var(--Color-background-main)' }}>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>학년·반</th>
                      <th style={{ textAlign: 'left', padding: '12px 16px', fontWeight: 600 }}>교사</th>
                      <th style={{ textAlign: 'right', padding: '12px 16px', fontWeight: 600 }}>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outOfConfigTeachers.map((t) => {
                      const hc = t.homeroomClass!;
                      return (
                        <tr key={t.uid} style={{ borderTop: '1px solid var(--Color-line-normal)' }}>
                          <td style={{ padding: '12px 16px' }}>
                            {hc.grade}학년 {hc.class}반
                          </td>
                          <td style={{ padding: '12px 16px' }}>
                            {t.name}{' '}
                            <span style={{ color: 'var(--Color-text-normal)', fontSize: '13px' }}>({t.email || t.uid})</span>
                          </td>
                          <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                            <Button
                              variant="line"
                              size="small"
                              onClick={() => handleClearHomeroom(t)}
                              disabled={clearingUid === t.uid}
                            >
                              {clearingUid === t.uid ? '처리 중…' : '담임 해제'}
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default HomeroomTeacherManagement;
